import React, { useMemo, useState } from 'react';
import { MapContainer, TileLayer, Polygon, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './CensusMap.css';
import useCensusTracts from './hooks/useCensusTracts';
import { MapSkeleton } from './CensusSkeletons';

// Nível hierárquico -> campo do setor censitário que identifica a unidade daquele nível.
const LEVELS = [
    { key: 'group', label: 'Grupos' },
    { key: 'sector', label: 'Setores' },
    { key: 'neighborhood', label: 'Bairros' },
    { key: 'id', label: 'Setores Censitários' }
];

const COLOR_SCALE = ['#deebf7', '#9ecae1', '#4292c6', '#08519c'];
const NO_DATA_COLOR = '#ced4da';
const SELECTED_COLOR = '#fd7e14';

// Borda de cada cor: um tom mais escuro do preenchimento, para os limites aparecerem sobre o mapa
// sem competir com as cores da escala.
const BORDER_BY_COLOR = {
    '#deebf7': '#6baed6',
    '#9ecae1': '#3182bd',
    '#4292c6': '#08519c',
    '#08519c': '#08306b',
    [NO_DATA_COLOR]: '#868e96',
    [SELECTED_COLOR]: '#c2410c'
};

export const formatNumber = value => (value === null || value === undefined ? '—' : value.toLocaleString('pt-BR'));

// Soma ignorando nulos (sigilo do IBGE ou tema não importado); null quando nenhum valor existe.
export function sumValues(values) {
    const known = values.filter(value => value !== null && value !== undefined);
    return known.length ? known.reduce((sum, value) => sum + value, 0) : null;
}

// Retângulo que envolve todos os setores; polygon pode ser Polygon ou MultiPolygon,
// e achatado alterna lat, lng, lat, lng...
export function boundsOf(tracts) {
    const coords = tracts.flatMap(tract => tract.polygon.flat(Infinity));
    const lats = coords.filter((_, index) => index % 2 === 0);
    const lngs = coords.filter((_, index) => index % 2 === 1);
    return [
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)]
    ];
}

function unitLabel(levelKey, unit) {
    return levelKey === 'id' ? `Setor Censitário ${unit}` : unit;
}

/**
 * Mapa dos setores censitários da cidade, agrupados por nível territorial e coloridos pela soma de `valueOf`
 * em cada área. `renderPanel` recebe a seleção ({ label, tracts, isCity }) e desenha os dados da área, abaixo do mapa;
 * `toolbar` são controles extras da página, exibidos na barra do mapa ao lado dos níveis.
 * `isLoading` permite à página segurar o skeleton até os seus próprios dados chegarem (ex.: valores da variável).
 */
function CensusMap({ cityId, year, valueOf, metricLabel, renderPanel, toolbar = null, isLoading = false }) {
    const { data: tracts = [], isPending, isError, error } = useCensusTracts(cityId, year);
    const [level, setLevel] = useState('neighborhood');
    const [selected, setSelected] = useState(null);

    const tractsByUnit = useMemo(() => {
        const grouped = {};
        tracts.forEach(tract => {
            (grouped[tract[level]] = grouped[tract[level]] || []).push(tract);
        });
        return grouped;
    }, [tracts, level]);

    const totalByUnit = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(tractsByUnit).map(([unit, unitTracts]) => [unit, sumValues(unitTracts.map(valueOf))])
            ),
        [tractsByUnit, valueOf]
    );

    const colorFor = useMemo(() => {
        const values = Object.values(totalByUnit).filter(value => value !== null);
        const min = Math.min(...values);
        const step = (Math.max(...values) - min) / COLOR_SCALE.length || 1;
        return value =>
            value === null
                ? NO_DATA_COLOR
                : COLOR_SCALE[Math.min(COLOR_SCALE.length - 1, Math.floor((value - min) / step))];
    }, [totalByUnit]);

    const bounds = useMemo(() => (tracts.length ? boundsOf(tracts) : null), [tracts]);

    if (!cityId) {
        return <div className="censo-notice">Selecione uma cidade para ver o mapa do Censo.</div>;
    }
    if (isError) {
        return <div className="censo-notice censo-notice-error">{error.message}</div>;
    }
    // isPending também cobre o ano ainda carregando (consulta aguardando), não só a busca em andamento.
    if (isPending || isLoading) {
        return <MapSkeleton />;
    }
    if (!tracts.length) {
        return (
            <div className="censo-notice">
                Nenhum setor censitário com desenho para esta cidade. Importe a malha de setores (GeoJSON) no
                módulo de arquivos.
            </div>
        );
    }

    const changeLevel = newLevel => {
        setLevel(newLevel);
        setSelected(null);
    };

    const selection = selected
        ? { label: unitLabel(level, selected), tracts: tractsByUnit[selected], isCity: false }
        : { label: 'Município (total)', tracts, isCity: true };

    return (
        <div className="censo-dashboard">
            <div className="censo-toolbar">
                <div className="censo-toolbar-group">
                    <span className="censo-toolbar-label" id="censo-level-label">
                        Nível territorial
                    </span>
                    <div className="censo-levels" role="group" aria-labelledby="censo-level-label">
                        {LEVELS.map(({ key, label }) => (
                            <button
                                key={key}
                                type="button"
                                className={`btn btn-sm ${level === key ? 'btn-info' : 'btn-outline-info'}`}
                                aria-pressed={level === key}
                                onClick={() => changeLevel(key)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                {toolbar && <div className="censo-toolbar-group censo-toolbar-grow">{toolbar}</div>}
            </div>

            <div className="censo-body">
                {/* key: remonta o mapa ao trocar de cidade, para reenquadrar nos novos setores. */}
                {/* zoomSnap fracionado: o enquadramento inicial ocupa o mapa todo, em vez de cair no
                    zoom inteiro de baixo e sobrar borda vazia em volta dos setores. */}
                <MapContainer
                    key={cityId}
                    bounds={bounds}
                    boundsOptions={{ padding: [8, 8] }}
                    zoomSnap={0.25}
                    zoomDelta={0.5}
                    preferCanvas
                    className="censo-map"
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {tracts.map(tract => {
                        const unit = tract[level];
                        const isSelected = unit === selected;
                        const fillColor = isSelected ? SELECTED_COLOR : colorFor(totalByUnit[unit]);
                        return (
                            <Polygon
                                key={tract.id}
                                positions={tract.polygon}
                                pathOptions={{
                                    // No nível de setor censitário cada setor tem contorno visível; acima dele
                                    // a borda é mais fina e clara, para a unidade aparecer como uma área só.
                                    color: BORDER_BY_COLOR[fillColor],
                                    weight: isSelected ? 2 : level === 'id' ? 1 : 0.6,
                                    opacity: isSelected || level === 'id' ? 0.9 : 0.6,
                                    fillColor,
                                    fillOpacity: 0.7
                                }}
                                eventHandlers={{ click: () => setSelected(isSelected ? null : unit) }}
                            >
                                <Tooltip sticky>
                                    {unitLabel(level, unit)}: {formatNumber(totalByUnit[unit])} {metricLabel}
                                </Tooltip>
                            </Polygon>
                        );
                    })}
                </MapContainer>

                <div className="censo-legend" aria-label={`Legenda: ${metricLabel}`}>
                    <span>Menos</span>
                    {COLOR_SCALE.map(color => (
                        <i key={color} style={{ background: color }} />
                    ))}
                    <span>Mais {metricLabel}</span>
                    <i style={{ background: NO_DATA_COLOR }} />
                    <span>Sem dado</span>
                </div>

                <section className="censo-map-data">
                    <h5>{selection.label}</h5>
                    {year && <p className="censo-panel-sub">Censo {year}</p>}
                    <p className="censo-panel-sub">
                        {selection.isCity
                            ? 'Clique em uma área do mapa para ver os indicadores'
                            : `${selection.tracts.length} setor(es) censitário(s)`}
                    </p>

                    {renderPanel(selection)}
                </section>
            </div>
        </div>
    );
}

export default CensusMap;
